import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  emptyAddress,
  policyholderComplement,
  type Address,
  type Policyholder,
  type PolicyholderAddressEntry,
} from '@/shared/api/resources';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../../shared/fieldError';
import { Bloco } from './shared';

/**
 * Listas do cadastro do tomador (P-TOM-C1): nomes fantasia, enderecos e telefones,
 * como o OnPoint devolve (Person/GetFantasyNames, Locations[] e Phones[]).
 *
 * O principal de cada lista e o que o cadastro mostra: tornar outro principal troca
 * o cadastro, e editar o cadastro edita o principal. O principal nao sai da lista;
 * para tira-lo, marque outro.
 */
export function ListasDoCadastro({
  tomador,
  podeEditar,
}: {
  tomador: Policyholder;
  podeEditar: boolean;
}) {
  return (
    <div className="flex flex-col gap-6 border-t border-line pt-4">
      <NomesFantasia tomador={tomador} podeEditar={podeEditar} />
      <Telefones tomador={tomador} podeEditar={podeEditar} />
      <Enderecos tomador={tomador} podeEditar={podeEditar} />
    </div>
  );
}

function useInvalidar(id: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['policyholder', id] });
    void queryClient.invalidateQueries({ queryKey: ['policyholders'] });
  };
}

function erroDaApi(erro: Error | null): ApiError | null {
  return erro instanceof ApiError ? erro : null;
}

function Principal({ principal }: { principal: boolean }) {
  return principal ? <Badge tone="brand">Principal</Badge> : <span>-</span>;
}

function Marcador({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (valor: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs font-semibold text-ink-700">
      <input
        type="checkbox"
        className="size-4"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

// --- Nomes fantasia ---------------------------------------------------------------

function NomesFantasia({
  tomador,
  podeEditar,
}: {
  tomador: Policyholder;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const [incluindo, setIncluindo] = useState(false);
  const [nome, setNome] = useState('');
  const [principal, setPrincipal] = useState(false);

  const lista = useQuery({
    queryKey: ['policyholder', tomador.id, 'trade-names'],
    queryFn: ({ signal }) => policyholderComplement.tradeNames(tomador.id, signal),
  });
  const incluir = useMutation({
    mutationFn: () =>
      policyholderComplement.addTradeName(tomador.id, {
        name: nome,
        is_main: principal,
      }),
    onSuccess: () => {
      toast.success('Nome fantasia incluido.');
      setIncluindo(false);
      setNome('');
      setPrincipal(false);
      invalidar();
    },
  });
  const tornarPrincipal = useMutation({
    mutationFn: (itemId: string) =>
      policyholderComplement.updateTradeName(tomador.id, itemId, { is_main: true }),
    onSuccess: () => {
      toast.success('Nome fantasia principal trocado.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const remover = useMutation({
    mutationFn: (itemId: string) =>
      policyholderComplement.removeTradeName(tomador.id, itemId),
    onSuccess: () => {
      toast.success('Nome fantasia removido.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const apiError = erroDaApi(incluir.error);

  return (
    <Bloco
      titulo="Nomes fantasia"
      acao={
        podeEditar ? (
          <Button size="sm" variant="secondary" onClick={() => setIncluindo(true)}>
            Adicionar nome fantasia
          </Button>
        ) : null
      }
    >
      {lista.error ? (
        <Alert tone="danger" title="Nao foi possivel carregar">
          {lista.error.message}
        </Alert>
      ) : null}
      <DataTable
        columns={[
          { key: 'nome', header: 'Nome fantasia', render: (n) => n.name },
          {
            key: 'principal',
            header: 'Principal',
            render: (n) => <Principal principal={n.is_main} />,
          },
          {
            key: 'quem',
            header: 'Incluido por',
            render: (n) => n.created_label || '-',
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (n) =>
              podeEditar && !n.is_main ? (
                <div className="flex gap-1">
                  <AcaoComIcone
                    acao="tornarPrincipal"
                    rotulo="Tornar principal"
                    onClick={() => tornarPrincipal.mutate(n.id)}
                  />
                  <AcaoComIcone
                    acao="remover"
                    rotulo="Remover"
                    onClick={() => remover.mutate(n.id)}
                  />
                </div>
              ) : null,
          },
        ]}
        items={lista.data ?? []}
        keyOf={(n) => n.id}
        empty={<EmptyState icon="file" title="Nenhum nome fantasia registrado" />}
      />
      <Modal
        open={incluindo}
        onClose={() => setIncluindo(false)}
        title="Adicionar nome fantasia"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIncluindo(false)}>
              Cancelar
            </Button>
            <Button
              loading={incluir.isPending}
              disabled={nome.trim() === ''}
              onClick={() => incluir.mutate()}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {incluir.error ? (
            <Alert tone="danger" title="Nao foi possivel incluir">
              {incluir.error.message}
            </Alert>
          ) : null}
          <Field
            label="Novo nome fantasia"
            value={nome}
            error={fieldErrorOf(apiError, 'name')}
            onChange={(event) => setNome(event.target.value)}
          />
          <Marcador
            label="Principal, o que o cadastro mostra"
            checked={principal}
            onChange={setPrincipal}
          />
        </div>
      </Modal>
    </Bloco>
  );
}

// --- Telefones ----------------------------------------------------------------------

function Telefones({
  tomador,
  podeEditar,
}: {
  tomador: Policyholder;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const [incluindo, setIncluindo] = useState(false);
  const [telefone, setTelefone] = useState('');
  const [principal, setPrincipal] = useState(false);

  const lista = useQuery({
    queryKey: ['policyholder', tomador.id, 'contacts'],
    queryFn: ({ signal }) => policyholderComplement.contacts(tomador.id, signal),
  });
  const incluir = useMutation({
    mutationFn: () =>
      policyholderComplement.addContact(tomador.id, {
        phone: telefone,
        is_main: principal,
      }),
    onSuccess: () => {
      toast.success('Telefone incluido.');
      setIncluindo(false);
      setTelefone('');
      setPrincipal(false);
      invalidar();
    },
  });
  const tornarPrincipal = useMutation({
    mutationFn: (itemId: string) =>
      policyholderComplement.updateContact(tomador.id, itemId, { is_main: true }),
    onSuccess: () => {
      toast.success('Telefone principal trocado.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const remover = useMutation({
    mutationFn: (itemId: string) =>
      policyholderComplement.removeContact(tomador.id, itemId),
    onSuccess: () => {
      toast.success('Telefone removido.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const apiError = erroDaApi(incluir.error);

  return (
    <Bloco
      titulo="Telefones"
      acao={
        podeEditar ? (
          <Button size="sm" variant="secondary" onClick={() => setIncluindo(true)}>
            Adicionar telefone
          </Button>
        ) : null
      }
    >
      {lista.error ? (
        <Alert tone="danger" title="Nao foi possivel carregar">
          {lista.error.message}
        </Alert>
      ) : null}
      <DataTable
        columns={[
          { key: 'telefone', header: 'Telefone', render: (c) => c.phone },
          {
            key: 'principal',
            header: 'Principal',
            render: (c) => <Principal principal={c.is_main} />,
          },
          {
            key: 'quem',
            header: 'Incluido por',
            render: (c) => c.created_label || '-',
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (c) =>
              podeEditar && !c.is_main ? (
                <div className="flex gap-1">
                  <AcaoComIcone
                    acao="tornarPrincipal"
                    rotulo="Tornar principal"
                    onClick={() => tornarPrincipal.mutate(c.id)}
                  />
                  <AcaoComIcone
                    acao="remover"
                    rotulo="Remover"
                    onClick={() => remover.mutate(c.id)}
                  />
                </div>
              ) : null,
          },
        ]}
        items={lista.data ?? []}
        keyOf={(c) => c.id}
        empty={<EmptyState icon="file" title="Nenhum telefone registrado" />}
      />
      <Modal
        open={incluindo}
        onClose={() => setIncluindo(false)}
        title="Adicionar telefone"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIncluindo(false)}>
              Cancelar
            </Button>
            <Button
              loading={incluir.isPending}
              disabled={telefone.trim() === ''}
              onClick={() => incluir.mutate()}
            >
              Salvar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {incluir.error ? (
            <Alert tone="danger" title="Nao foi possivel incluir">
              {incluir.error.message}
            </Alert>
          ) : null}
          <Field
            label="Novo telefone"
            placeholder="(00) 0000-0000"
            value={telefone}
            error={fieldErrorOf(apiError, 'phone')}
            onChange={(event) => setTelefone(event.target.value)}
          />
          <Marcador
            label="Principal, o que o cadastro mostra"
            checked={principal}
            onChange={setPrincipal}
          />
        </div>
      </Modal>
    </Bloco>
  );
}

// --- Enderecos -----------------------------------------------------------------------

function enderecoEmLinha(endereco: Address): string {
  return [
    endereco.street,
    endereco.number,
    endereco.complement,
    endereco.district,
    endereco.city,
    endereco.state,
    endereco.zip_code,
  ]
    .filter((parte) => parte && parte.trim() !== '')
    .join(', ');
}

function Enderecos({
  tomador,
  podeEditar,
}: {
  tomador: Policyholder;
  podeEditar: boolean;
}) {
  const toast = useToast();
  const invalidar = useInvalidar(tomador.id);
  const [incluindo, setIncluindo] = useState(false);
  const [endereco, setEndereco] = useState<Address>({ ...emptyAddress, city_code: '' });
  const [principal, setPrincipal] = useState(false);
  const [exterior, setExterior] = useState(false);
  const [pais, setPais] = useState('');

  const lista = useQuery({
    queryKey: ['policyholder', tomador.id, 'addresses'],
    queryFn: ({ signal }) => policyholderComplement.addresses(tomador.id, signal),
  });

  function limpar() {
    setIncluindo(false);
    setEndereco({ ...emptyAddress, city_code: '' });
    setPrincipal(false);
    setExterior(false);
    setPais('');
  }

  const incluir = useMutation({
    mutationFn: () =>
      policyholderComplement.addAddress(tomador.id, {
        // O endereco no exterior nao tem municipio do catalogo do IBGE.
        address: exterior ? { ...endereco, city_code: '' } : endereco,
        is_main: principal,
        is_international: exterior,
        country_code: exterior ? pais : 'BR',
      }),
    onSuccess: () => {
      toast.success('Endereco incluido.');
      limpar();
      invalidar();
    },
  });
  const alterar = useMutation({
    mutationFn: ({
      item,
      mudanca,
    }: {
      item: PolicyholderAddressEntry;
      mudanca: 'principal' | 'situacao';
    }) =>
      policyholderComplement.updateAddress(
        tomador.id,
        item.id,
        mudanca === 'principal' ? { is_main: true } : { is_active: !item.is_active },
      ),
    onSuccess: () => {
      toast.success('Endereco atualizado.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const remover = useMutation({
    mutationFn: (itemId: string) =>
      policyholderComplement.removeAddress(tomador.id, itemId),
    onSuccess: () => {
      toast.success('Endereco removido.');
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });
  const apiError = erroDaApi(incluir.error);

  return (
    <Bloco
      titulo="Enderecos"
      acao={
        podeEditar ? (
          <Button size="sm" variant="secondary" onClick={() => setIncluindo(true)}>
            Adicionar endereco
          </Button>
        ) : null
      }
    >
      {lista.error ? (
        <Alert tone="danger" title="Nao foi possivel carregar">
          {lista.error.message}
        </Alert>
      ) : null}
      <DataTable
        columns={[
          { key: 'ordem', header: 'Ordem', render: (a) => a.sequence },
          {
            key: 'endereco',
            header: 'Endereco',
            render: (a) =>
              a.is_international
                ? `${enderecoEmLinha(a.address)} (${a.country_code})`
                : enderecoEmLinha(a.address),
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (a) => (a.is_active ? 'Ativo' : 'Inativo'),
          },
          {
            key: 'principal',
            header: 'Principal',
            render: (a) => <Principal principal={a.is_main} />,
          },
          {
            key: 'acoes',
            header: 'Acoes',
            render: (a) =>
              podeEditar && !a.is_main ? (
                <div className="flex gap-1">
                  {a.is_active && !a.is_international ? (
                    <AcaoComIcone
                      acao="tornarPrincipal"
                      rotulo="Tornar principal"
                      onClick={() => alterar.mutate({ item: a, mudanca: 'principal' })}
                    />
                  ) : null}
                  <AcaoComIcone
                    acao={a.is_active ? 'desativar' : 'ativar'}
                    rotulo={a.is_active ? 'Desativar' : 'Ativar'}
                    onClick={() => alterar.mutate({ item: a, mudanca: 'situacao' })}
                  />
                  <AcaoComIcone
                    acao="remover"
                    rotulo="Remover"
                    onClick={() => remover.mutate(a.id)}
                  />
                </div>
              ) : null,
          },
        ]}
        items={lista.data ?? []}
        keyOf={(a) => a.id}
        empty={<EmptyState icon="file" title="Nenhum endereco registrado" />}
      />
      <Modal
        open={incluindo}
        onClose={limpar}
        title="Adicionar endereco"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={limpar}>
              Cancelar
            </Button>
            <Button loading={incluir.isPending} onClick={() => incluir.mutate()}>
              Salvar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {incluir.error ? (
            <Alert tone="danger" title="Nao foi possivel incluir">
              {incluir.error.message}
            </Alert>
          ) : null}
          <Marcador
            label="Endereco no exterior"
            checked={exterior}
            onChange={(valor) => {
              setExterior(valor);
              if (valor) setPrincipal(false);
            }}
          />
          {exterior ? (
            <Field
              label="Pais (sigla)"
              placeholder="PT"
              maxLength={2}
              value={pais}
              error={fieldErrorOf(apiError, 'country_code')}
              onChange={(event) => setPais(event.target.value.toUpperCase())}
            />
          ) : null}
          <AddressFields
            value={endereco}
            onChange={setEndereco}
            withCityCode={!exterior}
            legend="Endereco da lista"
          />
          {!exterior ? (
            <Marcador
              label="Principal, o que o cadastro mostra"
              checked={principal}
              onChange={setPrincipal}
            />
          ) : (
            <p className="text-2xs text-ink-500">
              O endereco no exterior nao pode ser o principal: o cadastro guarda
              endereco no Brasil.
            </p>
          )}
        </div>
      </Modal>
    </Bloco>
  );
}
